import React, { useRef, useState, useEffect, useCallback } from "react";
import styles from "styles.module.css";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import {
	ExcelExport,
	ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import { OverLayLoading } from "ui/Loading";
import { useTranslation } from "react-i18next";
import { useInfoStore } from "store/error";
import GridWithGroupPaging from "ui/grid/GridWithGroupPaging";
import { SalesNoteJournalItem } from "interface/sales";
import { PageTypePath } from "utils/pages";
// import "./style.css";
import { SalesNoteListGridColumn } from "./GridColumns";
import {
	getSalesNoteHeaderApi,
	getSalesNoteListApi,
	SalesNoteFilter,
} from "api/salesNote";
import { SalesNoteList } from "interface/salesNote";
import GridPageNoPaging from "ui/grid/GridPageNoPaging";
import Filter from "./Filter";
import { addDays, addWeeks } from "date-fns";
import SalesNoteListMobile from "./SalesNotesMobileList";

const pageSize = 60;
const take = 999999; // the same with the the custom grid

const SalesNoteListPage = () => {
	const { t } = useTranslation();
	const overlayRef = useRef<HTMLDivElement>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const mobileContainerRef = useRef<HTMLDivElement>(null);

	const gridRef = useRef<HTMLDivElement>(null);
	const mobileRequestInProgress = useRef(false);
	const mobileDataFinished = useRef(false);
	const mobileNextSkip = useRef(0);
	const [isMobileView, setIsMobileView] = useState(window.innerWidth < 1700);
	const [mobileData, setMobileData] = useState<SalesNoteList[]>([]);
	const [gridData, setGridData] = useState<SalesNoteList[]>([]);
	const [mobileLoading, setMobileLoading] = useState(false);

	const [filterOptions, setFilterOptions] = useState<SalesNoteFilter>({
		take: isMobileView ? pageSize : take,
		skip: 0,
		fromDate: addDays(new Date(), -3),
		toDate: new Date(),
		statusId: 4,
	});

	const pdfRef = useRef<GridPDFExport>(null);
	const excelRef = useRef<ExcelExport>(null);
	const mobileExcelRef = useRef<ExcelExport>(null);

	const { setError } = useInfoStore();

	useEffect(() => {
		if (isMobileView) return;
		setIsLoading(true);
		getGridData();
	}, [filterOptions, isMobileView]);

	const exportGridAsPDF = () => {
		const fromDate =
			filterOptions.fromDate instanceof Date
				? filterOptions.fromDate.toISOString()
				: "";
		const toDate =
			filterOptions.toDate instanceof Date
				? filterOptions.toDate.toISOString()
				: "";
		localStorage.setItem("fromDate", fromDate);
		localStorage.setItem("toDate", toDate);

		localStorage.setItem("buyerId", filterOptions.buyerId?.toString() ?? "");
		localStorage.setItem("vesselId",filterOptions.vesselId ?? "");

		window.open(`/${PageTypePath.Pdf}/0/7/`, "_blank");
	};

	const exportGridAsExcel = () => {
		if (isMobileView) {
			if (mobileExcelRef.current !== null) {
				mobileExcelRef.current.save();
			}
		} else {
			if (excelRef.current !== null) {
				excelRef.current.save();
			}
		}
	};

	const requestMobileData = (skip: number, take: number, resetData = false) => {
		if (mobileRequestInProgress.current || mobileDataFinished.current) {
			return;
		}

		mobileRequestInProgress.current = true;
		setMobileLoading(true);

		getSalesNoteListApi({
			...filterOptions,
			skip,
			take,
		})
			.then((response) => {
				const incoming = response?.data || [];

				if (incoming.length === 0) {
					mobileDataFinished.current = true;
					if (resetData) {
						setMobileData([]);
					}
					return;
				}

				mobileNextSkip.current = skip + take;

				if (resetData) {
					setMobileData(incoming);
				} else {
					setMobileData((prev) => [...prev, ...incoming]);
				}

				const loadedCount = skip + incoming.length;
				if (loadedCount >= (response?.totalCount || 0)) {
					mobileDataFinished.current = true;
				}
			})
			.finally(() => {
				mobileRequestInProgress.current = false;
				setMobileLoading(false);
			})
			.catch((err) => {
				console.log(err);
			});
	};

	const getGridData = () => {
		getSalesNoteListApi({
			...filterOptions,
		})
			.then((response) => {
				setGridData(response.data);
			})
			.catch((err) => {
				console.log(err);
			});
	};

	const handleMobileContainerScroll = (
		event: React.UIEvent<HTMLDivElement>,
	) => {
		const target = event.currentTarget;
		const remaining =
			target.scrollHeight - target.scrollTop - target.clientHeight;
		if (remaining < 200) {
			requestMobileData(mobileNextSkip.current, pageSize);
		}
	};

	useEffect(() => {
		const updateMobileView = () => setIsMobileView(window.innerWidth < 1600);
		window.addEventListener("resize", updateMobileView);
		return () => window.removeEventListener("resize", updateMobileView);
	}, []);

	useEffect(() => {
		if (!isMobileView) {
			return;
		}

		mobileDataFinished.current = false;
		mobileNextSkip.current = 0;
		setMobileData([]);

		requestMobileData(0, pageSize, true);
	}, [isMobileView, filterOptions]);

	return (
		<div className={`${styles.PageContainer} `}>
			{/* <div ref={overlayRef}>
				<OverLayLoading show={isLoading} />
			</div> */}
			<div className="page-header">
				<legend className={styles.BoxHeadingLarge}>{t("SalesNoteList")}</legend>
			</div>
			<ExcelExport
				ref={mobileExcelRef}
				data={mobileData.map((data) => ({
					...data,
					registrationTime: data.registrationTime
						? new Date(data.registrationTime)
						: null,
				}))}
				fileName="sluttseddelliste.xlsx"
			>
				<ExcelExportColumn
					field="displayNoteNumber"
					title={t("SalesNoteNumber")}
				/>
				<ExcelExportColumn field="noteVersion" title={t("V")} />

				<ExcelExportColumn
					field="receiverReceivingFacility"
					title={t("TerminalCode")}
				/>
				<ExcelExportColumn
					field="vesselRegistrationId"
					title={t("VesselRegistrationId")}
				/>
				<ExcelExportColumn field="vesselName" title={t("VesselName")} />

				<ExcelExportColumn field="fishingGear" title={t("FishingGear")} />
				<ExcelExportColumn
					field="registrationTime"
					title={t("DeliveryDateShort")}
					cellOptions={{ format: "dd.mm.yyyy", textAlign: "left" }}
				/>
				<ExcelExportColumn
					field="lastCatchDate"
					title={t("LastCatchDate")}
					cellOptions={{ format: "dd.mm.yyyy", textAlign: "left" }}
				/>
				<ExcelExportColumn field="catchArea" title={t("CatchArea")} />
				<ExcelExportColumn field="fishingGear" title={t("FishingGear")} />
				<ExcelExportColumn field="netLiveWeightKg" title={t("TotalWeight")} />
				<ExcelExportColumn field="roundWeightKg" title={t("RoundWeight")} />
				<ExcelExportColumn field="amount" title={t("TotalValue")} />
			</ExcelExport>

			<div ref={gridRef}>
				{isMobileView ? (
					<div
						ref={mobileContainerRef}
						style={{
							height: "calc(100vh - 280px)", // Reduced from 150px to fit screen better
							overflow: "auto",
							display: "flex",
							flexDirection: "column",
						}}
						onScroll={handleMobileContainerScroll}
					>
						{/* Filter scrolls with list */}
						<div style={{ flexShrink: 0 }}>
							<Filter
								setFilterOptions={setFilterOptions}
								filterOptions={filterOptions}
								exportAsPDF={exportGridAsPDF}
								exportAsExcel={exportGridAsExcel}
								pageSize={pageSize}
							/>
						</div>

						{/* Mobile List */}
						<div style={{ marginTop: 30, flex: 1 }}>
							<SalesNoteListMobile
								items={mobileData}
								loading={mobileLoading}
								onLoadMore={() =>
									requestMobileData(mobileNextSkip.current, pageSize)
								}
							/>
						</div>
					</div>
				) : (
					<>
						<Filter
							setFilterOptions={setFilterOptions}
							filterOptions={filterOptions}
							exportAsPDF={exportGridAsPDF}
							exportAsExcel={exportGridAsExcel}
							pageSize={take}
						/>
						<GridWithGroupPaging
							dataKey="displayNoteNumber"
							itemAboveHeight={90}
								gridData={gridData}
								unUsedHeight={ 280 }
							gridColumns={SalesNoteListGridColumn(gridData)}
							// filter={filterOptions}
							exportGridAsPDFRef={pdfRef}
							exportGridAsExcelRef={excelRef}
							// pageAccess={pageAccess}
							// apiMethod={getSalesNoteListApi}
							// handleSortChange={handleSortChange}
							// serverSortValue={sortState}
							pageable={false}
							sortable={{ allowUnsort: true, mode: "single" }}
							// lastColWidth="220px"
						/>
					</>
				)}
			</div>
		</div>
	);
};

export default SalesNoteListPage;
